import type { CSSProperties, ReactNode } from 'react';
import { TOUCH, colour, radius, space, text } from '../design/tokens.ts';

/**
 * The pieces every screen is made of.
 *
 * They exist so the design is applied once. Each one mirrors a pattern that
 * appears on most of the mockups in `maestro-handoff/design/`, down to the
 * radius and the shade — the quickest way to look unlike the design is for each
 * screen to approximate it.
 *
 * Everything you can touch is at least 44px tall (CLAUDE.md), and nothing
 * relies on colour alone: a status always carries its word.
 */

// ── Page ───────────────────────────────────────────────────────────────────

export function Page({
  eyebrow,
  title,
  description,
  note,
  action,
  children
}: {
  /** The small caps line, e.g. "MAESTRO · PROJECT BOARD". */
  eyebrow: string;
  title: string;
  description?: ReactNode | undefined;
  /** A second, smaller line: where a thing is configured, what it links to. */
  note?: ReactNode | undefined;
  /** The one primary action for this screen (screen 20's context row). */
  action?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        boxSizing: 'border-box',
        padding: `32px clamp(16px, 4vw, 64px) 64px`,
        display: 'flex',
        flexDirection: 'column',
        gap: space.section
      }}
    >
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.hair }}>
          <span style={{ ...text.eyebrow, color: colour.inkMuted }}>{eyebrow}</span>
          <h1 style={{ margin: 0, ...text.h1 }}>{title}</h1>
          {description && (
            <span style={{ ...text.body, color: colour.inkSoft, maxWidth: 780 }}>{description}</span>
          )}
          {note && <span style={{ ...text.small, color: colour.inkSoft }}>{note}</span>}
        </div>
        {action && <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>{action}</div>}
      </header>
      {children}
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────

export function Card({
  title,
  aside,
  children,
  style
}: {
  title?: ReactNode | undefined;
  /** The line on the right of the heading: a legend, a count, a hint. */
  aside?: ReactNode | undefined;
  children: ReactNode;
  style?: CSSProperties | undefined;
}) {
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.control,
        padding: space.card,
        background: colour.surface,
        border: `1px solid ${colour.edge}`,
        borderRadius: radius.card,
        ...style
      }}
    >
      {(title || aside) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: space.tight
          }}
        >
          {title && <h2 style={{ margin: 0, ...text.h2 }}>{title}</h2>}
          {aside && <span style={{ ...text.note, color: colour.inkSoft }}>{aside}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

// ── Button ─────────────────────────────────────────────────────────────────

export type ButtonTone = 'primary' | 'plain' | 'danger' | 'quiet';

const TONES: Record<ButtonTone, { bg: string; fg: string; edge: string }> = {
  primary: { bg: colour.accent, fg: '#ffffff', edge: colour.accent },
  plain: { bg: colour.surface, fg: colour.ink, edge: colour.edge },
  danger: { bg: colour.surface, fg: colour.staleInk, edge: colour.stale },
  quiet: { bg: 'transparent', fg: colour.link, edge: 'transparent' }
};

export function Button({
  children,
  onClick,
  tone = 'plain',
  disabled,
  /** Why it is off, said out loud rather than left to a grey button. */
  disabledReason,
  type = 'button',
  style
}: {
  children: ReactNode;
  onClick?: () => void | undefined;
  tone?: ButtonTone | undefined;
  disabled?: boolean | undefined;
  disabledReason?: string | undefined;
  type?: 'button' | 'submit' | undefined;
  style?: CSSProperties | undefined;
}) {
  const t = TONES[tone];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      {...(disabled && disabledReason ? { title: disabledReason } : {})}
      style={{
        minHeight: TOUCH,
        padding: '0 20px',
        border: `1px solid ${disabled ? colour.edge : t.edge}`,
        borderRadius: radius.control,
        background: disabled ? colour.surfaceSunk : t.bg,
        color: disabled ? colour.inkFaint : t.fg,
        font: text.bodyStrong.font,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style
      }}
    >
      {children}
    </button>
  );
}

/** Two or three buttons that pick one thing, as a single joined control. */
export function Switcher<T extends string>({
  options,
  value,
  onChange,
  label
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label?: string | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.label }}>
      {label && <span style={{ ...text.label, color: colour.inkMuted }}>{label}</span>}
      <div style={{ display: 'flex' }}>
        {options.map((option, i) => {
          const on = option.value === value;
          const first = i === 0;
          const last = i === options.length - 1;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(option.value)}
              style={{
                minHeight: TOUCH,
                padding: '0 18px',
                border: `1px solid ${colour.ink}`,
                ...(first ? {} : { borderLeft: 'none' }),
                borderRadius: `${first ? radius.control : 0}px ${last ? radius.control : 0}px ${last ? radius.control : 0}px ${first ? radius.control : 0}px`,
                background: on ? colour.ink : colour.surface,
                color: on ? '#ffffff' : colour.ink,
                font: text.bodyStrong.font,
                cursor: 'pointer'
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Chip ───────────────────────────────────────────────────────────────────

export function Chip({
  children,
  onClick,
  on,
  onRemove,
  label
}: {
  children: ReactNode;
  onClick?: () => void | undefined;
  on?: boolean | undefined;
  /** Makes it a removable active-filter chip, with the × and the aria-label. */
  onRemove?: () => void | undefined;
  label?: string | undefined;
}) {
  const interactive = onClick != null || onRemove != null;
  return (
    <button
      type="button"
      onClick={onRemove ?? onClick}
      {...(onRemove && label ? { 'aria-label': `Remove filter ${label}` } : {})}
      {...(onClick ? { 'aria-pressed': on === true } : {})}
      disabled={!interactive}
      style={{
        minHeight: TOUCH,
        padding: '0 14px',
        border: `1px solid ${on || onRemove ? colour.accent : colour.edge}`,
        borderRadius: radius.pill,
        background: on || onRemove ? colour.accentWash : colour.surface,
        color: on || onRemove ? colour.accentInk : colour.ink,
        font: text.smallStrong.font,
        cursor: interactive ? 'pointer' : 'default'
      }}
    >
      {children}
      {onRemove && ' ×'}
    </button>
  );
}

/** A small label for where a file is, or which cloud it is in. */
export function Tag({ children, tone }: { children: ReactNode; tone?: 'mono' | 'plain' }) {
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: radius.control,
        background: colour.surfaceSunk,
        border: `1px solid ${colour.edgeSoft}`,
        color: colour.inkSoft,
        font: tone === 'mono' ? text.mono.font : text.note.font,
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </span>
  );
}

// ── Status ─────────────────────────────────────────────────────────────────

import { statusColour, statusWord } from '../design/tokens.ts';

/**
 * A status, always with its word. Nothing in Maestro relies on colour alone
 * (docs/04, cross-cutting), so the colour is the second signal and never the
 * only one.
 */
export function Status({ value, detail }: { value: string; detail?: string }) {
  const c = statusColour[value] ?? statusColour.notmade!;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span
        style={{
          padding: '2px 10px',
          borderRadius: radius.pill,
          background: c.bg,
          border: `1px solid ${c.edge}`,
          color: c.fg,
          font: text.note.font,
          fontWeight: 600,
          whiteSpace: 'nowrap'
        }}
      >
        {statusWord[value] ?? value}
      </span>
      {detail && <span style={{ ...text.note, color: colour.inkSoft }}>{detail}</span>}
    </span>
  );
}

// ── Fields ─────────────────────────────────────────────────────────────────

export function Field({
  label,
  children,
  grow
}: {
  label: string;
  children: ReactNode;
  grow?: boolean | undefined;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.label,
        ...text.label,
        color: colour.inkMuted,
        ...(grow ? { flex: '1 1 260px' } : {})
      }}
    >
      {label}
      {children}
    </label>
  );
}

const controlStyle: CSSProperties = {
  minHeight: TOUCH,
  boxSizing: 'border-box',
  padding: '0 12px',
  border: `1px solid ${colour.edge}`,
  borderRadius: radius.control,
  background: colour.surface,
  color: colour.ink,
  font: text.body.font
};

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  type?: 'text' | 'password' | 'email' | undefined;
  autoComplete?: string | undefined;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...(placeholder ? { placeholder } : {})}
      {...(autoComplete ? { autoComplete } : {})}
      style={controlStyle}
    />
  );
}

/**
 * For a command template, which is long, wraps, and is read character by
 * character when something is wrong with it — so it is monospaced and never
 * silently cut off at the end of a line.
 */
export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 2
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  rows?: number | undefined;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      spellCheck={false}
      {...(placeholder ? { placeholder } : {})}
      style={{
        ...controlStyle,
        minHeight: 'unset',
        padding: '8px 12px',
        font: text.mono.font,
        lineHeight: 1.5,
        resize: 'vertical'
      }}
    />
  );
}

/** The dropdown arrow, drawn rather than left to the browser. */
const CHEVRON = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path d="M1 1.5 6 6.5l5-5" fill="none" stroke="${colour.inkMuted}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`
)}")`;

export function Select<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      style={{
        ...controlStyle,
        // The browser's own arrow sits at a fixed offset from the border and
        // ignores padding, which left it crowding the edge. Drawing it
        // ourselves is the only way to place it, and it looks the same in
        // every browser rather than three different ways.
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        backgroundImage: CHEVRON,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center',
        backgroundSize: '12px 8px',
        padding: '0 38px 0 12px',
        font: text.small.font
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ── Tiles ──────────────────────────────────────────────────────────────────

/** A summary tile, which on most screens is also a filter. */
export function Tile({
  count,
  label,
  swatch,
  on,
  onClick
}: {
  count: number | string;
  label: string;
  /** The colour this tile stands for, shown beside the number. */
  swatch?: string | undefined;
  on?: boolean | undefined;
  onClick?: () => void | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...(onClick ? { 'aria-pressed': on === true } : {})}
      disabled={onClick == null}
      style={{
        boxSizing: 'border-box',
        minHeight: 88,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        textAlign: 'left',
        background: colour.surface,
        border: on ? `2px solid ${colour.accent}` : `1px solid ${colour.edge}`,
        borderRadius: radius.card,
        color: colour.ink,
        cursor: onClick ? 'pointer' : 'default'
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: space.tight }}>
        {swatch && (
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              background: swatch,
              border: `1px solid ${colour.edgeStrong}`
            }}
          />
        )}
        <span style={text.figure}>{count}</span>
      </span>
      <span style={{ ...text.note, color: colour.inkSoft }}>{label}</span>
    </button>
  );
}

export function Tiles({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))',
        gap: space.control
      }}
    >
      {children}
    </div>
  );
}

// ── Pictures ───────────────────────────────────────────────────────────────

/**
 * A thumbnail, or a square that says there is not one yet. Rule 6: a row with
 * no picture is a row somebody has to read, so the gap is visible rather than
 * silently absent.
 */
export function Thumb({
  url,
  alt,
  size = 56,
  stale
}: {
  url: string | null | undefined;
  alt: string;
  size?: number | undefined;
  /** The picture was made from an older version of the thing it shows. */
  stale?: boolean | undefined;
}) {
  if (!url) {
    return (
      <div
        aria-label={`${alt} — no picture yet`}
        role="img"
        style={{
          width: size,
          height: Math.round(size * 0.5625),
          display: 'grid',
          placeItems: 'center',
          background: colour.surfaceSunk,
          border: `1px dashed ${colour.edge}`,
          borderRadius: radius.item,
          ...text.label,
          color: colour.inkFaint,
          flex: 'none'
        }}
      >
        none
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      style={{
        width: size,
        height: Math.round(size * 0.5625),
        objectFit: 'cover',
        borderRadius: radius.item,
        border: `1px solid ${stale ? colour.stale : colour.edge}`,
        background: colour.surfaceSunk,
        flex: 'none'
      }}
    />
  );
}

/** A row of things with a label, used all over the detail screens. */
export function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'baseline' }}>
      <span style={{ ...text.label, color: colour.inkMuted, minWidth: 110 }}>{label}</span>
      <span style={{ ...text.small, color: colour.ink }}>{children}</span>
    </div>
  );
}
